// Test: V_CLAP_PDP_08 | Founder and Reply Slides in the PDP Gallery
// Var A / Var B insert two image slides (+ thumbnails) into the PDP gallery at positions 2 and 3.
// Slide 2 differs per variant, slide 3 is shared. Control's gallery is never touched — the
// slides only exist in the DOM once a variant class is on <body>.
(() => {
  const FILES_URL = 'https://cdn.shopify.com/s/files/1/0646/7233/3995/files/';
  const SLIDE_3 = { file: 'slide_3_-_var_a-b.webp?v=1793704419', alt: 'Customer review with a reply from Kate and Jack' };
  const VARIANTS = {
    'c-vClapPdp08VarA': [
      { file: 'slide_2_-_var_a.webp?v=1793704419', alt: 'Meet Kate and Jack, founders of Clean Like a Pro' },
      SLIDE_3,
    ],
    'c-vClapPdp08VarB': [
      { file: 'slide_2_-_var_b.webp?v=1793704419', alt: 'Meet Kate and Jack, founders of Clean Like a Pro' },
      SLIDE_3,
    ],
  };
  // Slides are inserted after the first product media item.
  const INSERT_AFTER = 1;
  // Source images are 2620x2620; the zoom view uses the same 1946px width as the theme's own slides.
  const ZOOM_WIDTH = 1946;
  const ZOOM_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-zoom icon--medium" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.875 18.75C15.2242 18.75 18.75 15.2242 18.75 10.875C18.75 6.52576 15.2242 3 10.875 3C6.52576 3 3 6.52576 3 10.875C3 15.2242 6.52576 18.75 10.875 18.75Z"/><path d="M16.4434 16.4453L20.9997 21.0016"/></svg>';

  // Same markup as snippets/icon-slider-prev.liquid / icon-slider-next.liquid.
  const ARROW_PREV =
    '<svg class="icon icon-slider-prev icon--medium" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.75 16.2505L6.5 10.0005L12.75 3.75049" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ARROW_NEXT =
    '<svg class="icon icon-slider-prev icon--medium" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 3.74121L13.75 9.99121L7.5 16.2412" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  let done = false;

  const imageUrl = (file, width) => `${FILES_URL}${file}&width=${width}`;

  // Markup mirrors snippets/product-thumbnail.liquid (image branch). The inline --aspect-ratio
  // style is required: theme.css only reserves the slide's height for [style*="--aspect-ratio"],
  // without it the slide has no height until the image loads and the gallery collapses.
  const buildSlide = ({ file, alt }, i, zoomEnabled) => {
    const id = `c-pdp08-${i + 1}`;
    const slide = document.createElement('div');
    slide.className = 'product__media-item swiper-slide c-pdp08-slide';
    slide.setAttribute('role', 'listitem');
    slide.tabIndex = -1;
    slide.dataset.mediaId = id;
    slide.dataset.mediaType = 'image';
    slide.dataset.src = imageUrl(file, ZOOM_WIDTH);
    slide.dataset.pswpWidth = ZOOM_WIDTH;
    slide.dataset.pswpHeight = ZOOM_WIDTH;
    slide.innerHTML = `
      <div class="product__media-container blocks-radius media-type-image" style="--aspect-ratio: 1;">
        <div class="product__media media-wrapper">
          <img
            src="${imageUrl(file, 1200)}"
            srcset="${[620, 980, 1240, 1620].map((w) => `${imageUrl(file, w)} ${w}w`).join(', ')}"
            sizes="(max-width: 768px) calc(100vw - 3rem), 50vw"
            alt="${alt}"
            width="2620"
            height="2620"
            loading="eager"
          >
        </div>
        ${
          zoomEnabled
            ? `<span class="product__media-icon" aria-hidden="true">${ZOOM_ICON}</span>
        <button class="product__media-zoom btn--inherit js-photoswipe--zoom" type="button" aria-haspopup="dialog" data-media-id="${id}" data-media-type="image">
          <span class="visually-hidden">${alt}</span>
        </button>`
            : ''
        }
      </div>`;
    return slide;
  };

  const buildThumbItem = (src, alt, target) => {
    const thumb = document.createElement('div');
    thumb.className = 'product__thumbs-item swiper-slide c-pdp08-thumb';
    thumb.dataset.target = target;
    thumb.innerHTML = `
      <div class="product__thumbs-container medium-blocks-radius media-wrapper">
        <div class="product__thumbs-inner" style="--aspect-ratio: 1">
          <img src="${src}" alt="${alt}" width="300" height="300" loading="lazy">
        </div>
      </div>`;
    return thumb;
  };

  const buildThumb = ({ file, alt }, i) => buildThumbItem(imageUrl(file, 300), alt, `c-pdp08-${i + 1}`);

  // Products with a single media item are rendered by product-media-gallery.liquid without
  // arrows, without thumbnails and with the desktop slider off (data-only-image="true"), so our
  // extra slides would just sit hidden in a non-scrolling row on desktop. Add the same controls
  // and thumbnail strip the snippet renders for 2+ media, then re-init the gallery as a slider.
  const enableSliderForSingleMedia = (gallery, anchorSlide, images) => {
    const sectionId = gallery.id.replace('MediaGallery-', '');
    const mobileThumbs = gallery.dataset.enableMobileThumbnails === 'true';
    const viewerWrap = gallery.querySelector('.product__media-gallery-viewer-wrap');
    if (!viewerWrap) return;

    if (!mobileThumbs) viewerWrap.classList.add('slider-controls--below', 'slider-controls--grouped-mobile');
    viewerWrap.insertAdjacentHTML(
      'beforeend',
      `<div class="slider-controls--group">
        <div class="slider-controls">
          <button type="button" class="swiper-button-prev btn" name="previous" aria-label="Previous slide">${ARROW_PREV}</button>
          <button type="button" class="swiper-button-next btn" name="next" aria-label="Next slide">${ARROW_NEXT}</button>
          ${mobileThumbs ? '' : '<div class="swiper-pagination"></div>'}
        </div>
      </div>`
    );

    const thumbs = document.createElement('div');
    thumbs.id = `GalleryThumbnails-${sectionId}`;
    thumbs.className = `product__media-gallery-thumbails swiper${mobileThumbs ? '' : ' hidden md:block'}`;
    thumbs.innerHTML = `<div id="Slider-Thumbnails-${sectionId}" class="thumbnail-list swiper-wrapper"></div>`;
    const firstImg = anchorSlide.querySelector('img');
    const firstThumbSrc = (anchorSlide.dataset.src || firstImg?.src || '').replace(/width=\d+/, 'width=300');
    thumbs.firstElementChild.append(
      buildThumbItem(firstThumbSrc, firstImg?.alt || '', anchorSlide.dataset.mediaId),
      ...images.map(buildThumb)
    );
    gallery.append(thumbs);

    gallery.dataset.onlyImage = 'false';
    gallery.dataset.enableDesktopSlider = 'true';
    if (typeof gallery.sliderInstance === 'object') gallery.destroySlider();
    gallery.thumbsInstance = false;
    // init() re-reads the data attributes and DOM: builds thumbs + main slider (mobile and
    // desktop) and rebuilds the zoom lightbox with prev/next arrows and our slides in it.
    gallery.init();
  };

  // The zoom lightbox opens dataSource[data-media-index] and, on close, slides the gallery to
  // that same index — so every slide's index must equal its DOM position once ours are in.
  const renumberSlides = (mediaList) => {
    mediaList.querySelectorAll('.product__media-item').forEach((slide, index) => {
      slide.dataset.mediaIndex = index;
      const zoomButton = slide.querySelector('.js-photoswipe--zoom');
      if (zoomButton) zoomButton.dataset.mediaIndex = index;
    });
  };

  // If the lightbox was already built before our slides existed, add their entries in place
  // (media-gallery.js rebuilds it from the DOM on its next init(), e.g. on variant change).
  const addToLightbox = (gallery, slides) => {
    const dataSource = gallery.lightbox?.options?.dataSource;
    if (!Array.isArray(dataSource)) return;
    const entries = slides.map((slide) => ({
      id: slide.dataset.mediaIndex,
      src: slide.dataset.src,
      width: slide.dataset.pswpWidth,
      height: slide.dataset.pswpHeight,
      mediaType: 'image',
    }));
    dataSource.splice(INSERT_AFTER, 0, ...entries);
    dataSource.forEach((entry, index) => (entry.id = String(index)));
  };

  // media-gallery.js maps a variant's featured image to a slide by its product media position,
  // which is off by our inserted slides. Look the slide up by media id instead.
  const patchSetActiveMedia = (gallery) => {
    const original = gallery.setActiveMedia.bind(gallery);
    gallery.setActiveMedia = (variant) => {
      const slider = gallery.sliderInstance?.slider;
      const mediaId = variant?.featured_media?.id;
      if (slider && mediaId) {
        const index = [...slider.slides].findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index > -1) {
          slider.slideToLoop(index, 0, false);
          return;
        }
      }
      original(variant);
    };
  };

  // Custom event: founder_slide_view — once per page, when the founder slide (slot 2) is at least
  // half visible. IntersectionObserver accounts for the gallery's overflow clipping, so this
  // catches both swiping to the slide and scrolling it into view on stacked desktop layouts.
  const trackFounderSlideView = (founderSlide) => {
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        io.disconnect();
        window.igEvents = window.igEvents || [];
        window.igEvents.push({ event: 'founder_slide_view' });
      },
      { threshold: 0.5 }
    );
    io.observe(founderSlide);
  };

  const run = () => {
    if (done) return;
    const variantClass = Object.keys(VARIANTS).find((cls) => document.body.classList.contains(cls));
    if (!variantClass) return;

    // Main PDP gallery only — the quick-view section renders its own media-gallery on the page too.
    const gallery = document.querySelector('media-gallery[id^="MediaGallery-"]:not([id*="quick-view"])');
    const mediaList = gallery?.querySelector('[id^="Slider-Gallery-"]');
    const anchorSlide = mediaList?.querySelectorAll('.product__media-item')[INSERT_AFTER - 1];
    if (!anchorSlide || typeof gallery.setActiveMedia !== 'function') return;

    done = true;
    observer.disconnect();

    // product-info.js already slid the gallery to the selected variant's image on page load,
    // before these slides existed. Remember that slide so we can return to it after inserting
    // (Swiper keeps the numeric index, which would otherwise now point two slides earlier).
    const activeSlider = gallery.sliderInstance?.slider;
    const activeSlide = activeSlider ? [...activeSlider.slides][activeSlider.activeIndex] : null;

    const images = VARIANTS[variantClass];
    const zoomEnabled = gallery.dataset.enableImageZoom === 'true';
    const slides = images.map((image, i) => buildSlide(image, i, zoomEnabled));
    anchorSlide.after(...slides);
    renumberSlides(mediaList);

    patchSetActiveMedia(gallery);
    trackFounderSlideView(slides[0]);

    if (gallery.dataset.onlyImage === 'true') {
      enableSliderForSingleMedia(gallery, anchorSlide, images);
      return;
    }

    const anchorThumb = gallery.querySelectorAll('.thumbnail-list .product__thumbs-item')[INSERT_AFTER - 1];
    if (anchorThumb) anchorThumb.after(...images.map(buildThumb));

    addToLightbox(gallery, slides);

    // Gallery may already be initialised (it inits when scrolled into view): re-measure both
    // Swipers. If it isn't yet, Swiper picks the new slides up on init.
    gallery.thumbsInstance?.slider?.update?.();
    const slider = gallery.sliderInstance?.slider;
    if (slider) {
      slider.update();
      const restoredIndex = activeSlide ? [...slider.slides].indexOf(activeSlide) : -1;
      if (restoredIndex > -1 && restoredIndex !== slider.activeIndex) slider.slideTo(restoredIndex, 0); // callbacks on so the thumbnail highlight follows
    }
  };

  // c-intelligems-tests.js adds the variant class once both DOMContentLoaded and ig:ready
  // have fired, which can be before or after this deferred script runs.
  const observer = new MutationObserver(run);
  observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
